import React from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { SubscriptionPlan } from "@/lib/types";

const featuresSchema = z.object({
  currency_conversion: z.boolean().default(false),
  multiple_currencies: z.boolean().default(false),
  price_comparison: z.boolean().default(false),
  grouped_contracts: z.boolean().default(false),
  contract_services: z.boolean().default(false),
  reminders: z.boolean().default(false),
  payment_type_selection: z.boolean().default(false),
  dashboard: z.boolean().default(false),
  contract_pdf_email: z.boolean().default(false)
});

// Create the form schema
const formSchema = z.object({
  name: z.string().min(3, {
    message: "Plan name must be at least 3 characters.",
  }),
  description: z.string().optional(),
  plan_type: z.enum(["free", "basic", "premium", "enterprise"]),
  monthly_price: z.coerce.number().min(0),
  annual_price: z.coerce.number().min(0).optional().nullable(),
  is_active: z.boolean().default(true),
  show_on_landing: z.boolean().default(false),
  features: featuresSchema,
});

type FormValues = z.infer<typeof formSchema>;

interface SubscriptionPlanFormProps {
  plan: SubscriptionPlan | null;
  onSubmit: (data: FormValues) => void;
  onCancel: () => void;
}

export function SubscriptionPlanForm({ plan, onSubmit, onCancel }: SubscriptionPlanFormProps) {
  // Set up form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: plan
      ? {
          name: plan.name,
          description: plan.description || "",
          plan_type: plan.plan_type,
          monthly_price: plan.monthly_price,
          annual_price: plan.annual_price || null,
          is_active: plan.is_active,
          show_on_landing: plan.show_on_landing,
          features: {
            currency_conversion: plan.features?.currency_conversion || false,
            multiple_currencies: plan.features?.multiple_currencies || false,
            price_comparison: plan.features?.price_comparison || false,
            grouped_contracts: plan.features?.grouped_contracts || false,
            contract_services: plan.features?.contract_services || false,
            reminders: plan.features?.reminders || false,
            payment_type_selection: plan.features?.payment_type_selection || false,
            dashboard: plan.features?.dashboard || false,
            contract_pdf_email: plan.features?.contract_pdf_email || false,
          },
        }
      : {
          name: "",
          description: "",
          plan_type: "basic",
          monthly_price: 0,
          annual_price: null,
          is_active: true,
          show_on_landing: false,
          features: {
            currency_conversion: false,
            multiple_currencies: false,
            price_comparison: false,
            grouped_contracts: false,
            contract_services: false,
            reminders: false,
            payment_type_selection: false,
            dashboard: false,
            contract_pdf_email: false,
          },
        },
  });

  // Form submission handler
  const handleSubmit = (data: FormValues) => {
    onSubmit(data);
  };

  // Features configuration
  const features = [
    {
      id: "currency_conversion",
      name: "Currency Conversion",
      description: "Convert between different currencies",
    },
    {
      id: "multiple_currencies",
      name: "Multiple Currencies",
      description: "Support for multiple currencies in contracts",
    },
    {
      id: "price_comparison",
      name: "Price Comparison",
      description: "Compare prices between contracts",
    },
    {
      id: "grouped_contracts",
      name: "Grouped Contracts",
      description: "Group contracts together",
    },
    {
      id: "contract_services",
      name: "Contract Services",
      description: "Add services to contracts",
    },
    {
      id: "reminders",
      name: "Reminders",
      description: "Set reminders for contract expiration",
    },
    {
      id: "payment_type_selection",
      name: "Payment Type Selection",
      description: "Choose from multiple payment types",
    },
    {
      id: "dashboard",
      name: "Dashboard",
      description: "Access to dashboard analytics",
    },
    {
      id: "contract_pdf_email",
      name: "Contract PDF Email",
      description: "Send contract PDF via email",
    },
  ];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Plan Information */}
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Premium Plan" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="A detailed description of the plan features"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="plan_type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan Type</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-wrap gap-4"
                    >
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="free" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          Free
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="basic" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          Basic
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="premium" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          Premium
                        </FormLabel>
                      </FormItem>
                      <FormItem className="flex items-center space-x-2 space-y-0">
                        <FormControl>
                          <RadioGroupItem value="enterprise" />
                        </FormControl>
                        <FormLabel className="font-normal cursor-pointer">
                          Enterprise
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="monthly_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Monthly Price</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0"
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="annual_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Annual Price</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0"
                        value={field.value === null ? "" : field.value}
                        onChange={(e) =>
                          field.onChange(
                            e.target.value === ""
                              ? null
                              : Number(e.target.value)
                          )
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Annual pricing (optional)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>
                        Make this plan available for users to subscribe
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="show_on_landing"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Show on landing page</FormLabel>
                      <FormDescription>
                        Display this plan on the landing page pricing section
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Features */}
          <Card className="p-6">
            <h3 className="text-lg font-medium mb-4">Plan Features</h3>
            <div className="space-y-4">
              {features.map((feature) => (
                <FormField
                  key={feature.id}
                  control={form.control}
                  name={`features.${feature.id as keyof typeof featuresSchema.shape}`}
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-3 space-y-0 rounded-md border p-3">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>{feature.name}</FormLabel>
                        <FormDescription>
                          {feature.description}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
              ))}
            </div>
          </Card>
        </div>

        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">
            {plan ? "Update Plan" : "Create Plan"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
